/**
 * LE RÉSEAU, VU DU LIVRE (§14).
 *
 * Deux choses seulement, et la seconde est la vraie :
 *
 * 1. **Ce que le navigateur annonce** (`navigator.onLine`). C'est bon marché et c'est faux la
 *    moitié du temps : un portable accroché à un point d'accès qui ne route rien se dit « en
 *    ligne » — il l'est, au sens du câble.
 * 2. **Ce que le dernier appel a répondu.** Un `fetch` qui échoue sans réponse HTTP est la seule
 *    preuve qui vaille : le serveur n'est pas joignable, quoi qu'en dise la carte réseau. C'est
 *    `api/client.ts` qui le signale ici, et un appel qui aboutit le lève aussitôt.
 *
 * IL N'Y A PAS DE FILE DE GESTES EN ATTENTE, et il n'y en aura pas (§14.1). Une action mise en
 * réserve pour être rejouée à la reconnexion, c'est un coup porté sans son joueur — et dans un jeu
 * où la mort est définitive, c'est le joueur qui apprend une heure trop tard qu'il est mort. Hors
 * ligne, les gestes qui écrivent restent lisibles et désactivés, avec la raison écrite.
 *
 * Le module ne dépend pas de React : il expose un abonnement, et `useEnLigne` s'y branche.
 */
import { useSyncExternalStore } from "react";

/** Vrai tant qu'on n'a pas la preuve du contraire : on ne commence pas une visite en annonçant une
 * panne, et le premier appel tranchera de toute façon. */
let joignable = true;
const abonnes = new Set<() => void>();

function previens(): void {
  for (const abonne of abonnes) abonne();
}

function duNavigateur(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

export function enLigne(): boolean {
  return joignable && duNavigateur();
}

/** Appelé par le client d'API : un appel qui n'a pas eu de réponse HTTP du tout. */
export function serveurInjoignable(): void {
  if (!joignable) return;
  joignable = false;
  previens();
}

/** Appelé par le client d'API dès qu'une réponse arrive — même un refus. Un 403 prouve que le
 * serveur est là, ce qui est tout ce qu'on demande ici. */
export function serveurJoignable(): void {
  if (joignable) return;
  joignable = true;
  previens();
}

if (typeof window !== "undefined") {
  // Le navigateur qui retrouve le réseau lève le doute tout de suite : on ne fait pas attendre le
  // prochain appel pour effacer un bandeau qui n'a plus lieu d'être.
  window.addEventListener("online", () => {
    joignable = true;
    previens();
  });
  window.addEventListener("offline", previens);
}

function abonne(rappel: () => void): () => void {
  abonnes.add(rappel);
  return () => abonnes.delete(rappel);
}

export function useEnLigne(): boolean {
  return useSyncExternalStore(abonne, enLigne, () => true);
}

/**
 * L'AGENT DE SERVICE NE S'INSTALLE QU'EN PRODUCTION. En développement, Vite sert les modules un par
 * un et les remplace à chaud : un cache par-dessus rendrait le rechargement à chaud imprévisible,
 * et l'on passerait des soirées à déboguer une version d'il y a dix minutes.
 */
export function poseLAgentDeService(): void {
  if (!import.meta.env.PROD) return;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    // Un enregistrement qui échoue n'est pas une panne du jeu : on perd le hors-ligne, pas la
    // partie. On ne montre donc rien au joueur.
    void navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  });
}
